import { useEffect, useState } from "preact/hooks";
import { MODERATION_TARGET } from "../demo-v2/seed.js";
import { brandColors } from "./brand-scene.js";
import type { FlowCopy, WallCopy } from "./content.js";
import { BEFORE, EARLIER, type WallState } from "./flow.js";
import { MenuIcon } from "./icons.js";
import { DEMO_PATH, type TourCopy } from "./planner-content.js";
import { CameraScreen, InviteMock, Phone, SentScreen, SHOT_SRC } from "./screens.js";
import { pad, useScrollSteps } from "./scroll-steps.js";
import { ScrollCue, SnapStops, StepRail } from "./step-rail.js";
import { AlbumMock, BrandMock, ModerationMock, PanelMock } from "./tour-screens.js";
import { Wall } from "./wall.js";

const ACCENT = "green";
const WALL_STEP = 4;
const MODERATION_STEP = 5;
const HIDE_DELAY = 1400;

export function tourWall(step: number, guest: string, hidden: boolean): WallState {
  if (step < WALL_STEP) {
    return {
      featured: { key: BEFORE.id, src: BEFORE.large, author: BEFORE.author },
      recent: EARLIER,
      count: 17,
    };
  }
  const recent = [BEFORE, ...EARLIER];
  return {
    featured: { key: "guest", src: SHOT_SRC, author: guest },
    recent: hidden ? recent.filter((photo) => photo.id !== MODERATION_TARGET) : recent,
    count: hidden ? 17 : 18,
  };
}

export interface TourProps {
  readonly copy: TourCopy;
  readonly flow: FlowCopy;
  readonly wall: WallCopy;
}

export function Tour({ copy, flow, wall }: TourProps) {
  const total = copy.steps.length;
  const { track, live, step, idle, go, next } = useScrollSteps(total);
  const [hideDone, setHideDone] = useState(false);
  const colors = brandColors(ACCENT);
  const guest = `${flow.guest[0]} ${flow.guest[1]}`;
  const eventName = flow.scan;

  useEffect(() => {
    if (step !== MODERATION_STEP) {
      setHideDone(step > MODERATION_STEP);
      return;
    }
    setHideDone(false);
    const timer = setTimeout(() => setHideDone(true), HIDE_DELAY);
    return () => clearTimeout(timer);
  }, [step]);

  const screen = tourWall(step, guest, hideDone);
  const page = (index: number) => (index === step ? "phone-page is-on" : "phone-page");

  return (
    <section
      class={live ? "flow is-tour flow-v-a is-snap is-live" : "flow is-tour flow-v-a is-snap"}
      id="demo"
      style={{ "--steps": total }}
      data-step={step}
    >
      <div class="flow-track" ref={track}>
        <SnapStops total={total} />
        <div class="flow-pin">
          <div class="flow-panel" data-theme="dark">
            <div class="flow-copy">
              <p class="micro">{copy.label}</p>
              <h2 class="flow-title serif">{copy.title}</h2>
              <ol class="flow-steps">
                {copy.steps.map((item, index) => (
                  <li
                    key={item.title}
                    class={index === step ? "flow-step is-on" : "flow-step"}
                    aria-current={index === step ? "step" : undefined}
                  >
                    <p class="flow-tag">
                      {pad(index + 1)} / {item.role}
                    </p>
                    <h3 class="flow-step-title">{item.title}</h3>
                    <p class="flow-step-body">{item.body}</p>
                  </li>
                ))}
              </ol>
              <div class="flow-ticks">
                {copy.steps.map((item, index) => (
                  <button
                    key={item.title}
                    type="button"
                    class={index === step ? "flow-tick is-on" : "flow-tick"}
                    aria-label={`${pad(index + 1)} ${item.title}`}
                    aria-pressed={index === step}
                    onClick={() => go(index)}
                  >
                    <span class="flow-tick-bar" />
                    {pad(index + 1)}
                  </button>
                ))}
              </div>
              <a class="pill pill-ink flow-cta" href={DEMO_PATH}>
                {copy.cta}
                <MenuIcon name="arrow" size={16} />
              </a>
            </div>
            <div class="scene" aria-hidden="true" onClick={next}>
              <div class="scene-tv">
                <Wall
                  copy={wall}
                  host={copy.brand}
                  accent={colors.ring}
                  count={screen.count}
                  featured={screen.featured}
                  recent={screen.recent}
                />
              </div>
              <div class="scene-phone">
                <Phone>
                  <div class={page(0)}>
                    <BrandMock
                      copy={copy.screens}
                      invitedBy={flow.invitedBy}
                      name={copy.brand}
                      accent={ACCENT}
                      line={colors.line}
                      typing={live && step === 0}
                    />
                  </div>
                  <div class={page(1)}>
                    <PanelMock copy={copy.screens} name={copy.brand} eventName={eventName} />
                  </div>
                  <div class={page(2)}>
                    <InviteMock
                      copy={flow}
                      host={copy.brand}
                      accent={colors.button}
                      line={colors.line}
                      typing={live && step === 2}
                    />
                  </div>
                  <div class={page(3)}>
                    <CameraScreen copy={flow} flash={live && step === 3} />
                  </div>
                  <div class={page(4)}>
                    <SentScreen copy={flow} />
                  </div>
                  <div class={page(5)}>
                    <ModerationMock copy={copy.screens} done={hideDone} />
                  </div>
                  <div class={page(6)}>
                    <AlbumMock
                      copy={copy.screens}
                      name={copy.brand}
                      eventName={eventName}
                      line={colors.line}
                    />
                  </div>
                </Phone>
              </div>
            </div>
            <StepRail labels={copy.steps.map((item) => item.role)} step={step} go={go} />
            <ScrollCue copy={flow} step={step} total={total} shown={idle} next={next} />
            <p
              class={idle ? "flow-nudge is-on" : "flow-nudge"}
              aria-hidden="true"
              data-testid="flow-nudge"
            >
              <span class="flow-nudge-arrow">
                <MenuIcon name="chevron" size={16} />
              </span>
              {step < total - 1 ? copy.nudge : copy.nudgeLast}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
